import { useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { WorkflowComponentsSearch, type AdvancedSearchConfig } from './WorkflowComponentsSearch';

export interface WorkflowComponentsControllerProps {
    tenant?: string;
    title?: string;
    initialType?: string;
    propertyList: Array<{ value: string; label: string }>;
    columnsByCategory?: Record<string, Array<{ field: string; title: string }>>;
    onToggleColumn?: (fieldName: string) => void;
    onApply: (config: AdvancedSearchConfig) => void;
    onClear: () => void;
}

export const WorkflowComponentsController: React.FC<WorkflowComponentsControllerProps> = ({
    tenant,
    title = 'Workflow Components Search',
    initialType,
    propertyList,
    columnsByCategory,
    onToggleColumn,
    onApply,
    onClear,
}) => {
    const apolloClient = useApolloClient();
    const [allKinds, setAllKinds] = useState<string[]>([
        'WorkflowDefinition', 'Instance', 'Signal', 'ExecutionResult', 'TimelineItem', 'Task', 'JobStatus', 'ActorDescriptor', 'TransitionCatalog', 'Policy', 'Context', 'Schedule', 'Resource', 'Integration', 'Escalation', 'OptimizationRecommendation'
    ]);
    const [selectedKinds, setSelectedKinds] = useState<string[]>(() => initialType ? [initialType] : allKinds);
    const [viewMode, setViewMode] = useState<'compact' | 'standard' | 'detailed'>('standard');
    const [searchProperty, setSearchProperty] = useState<string>('name');
    const [fullText, setFullText] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [live, setLive] = useState<boolean>(false);
    const [valueSuggestions, setValueSuggestions] = useState<string[]>([]);
    const [actorSuggestions, setActorSuggestions] = useState<string[]>([]);

    // Dynamic kinds
    useEffect(() => {
        const run = async () => {
            try {
                const query = gql`query FormsKinds { listKinds }`;
                const res = await apolloClient.query<Record<string, any>>({ query });
                const kinds = (res?.data?.listKinds as string[]) || [];
                if (Array.isArray(kinds) && kinds.length) {
                    setAllKinds(kinds);
                    if (!initialType) setSelectedKinds(kinds);
                }
            } catch {}
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dictionary suggestions based on selected kind and property
    useEffect(() => {
        const run = async () => {
            try {
                const kind = selectedKinds && selectedKinds.length ? selectedKinds[0] : initialType || 'Instance';
                if (!searchProperty) { setValueSuggestions([]); return; }
                const DICT_CAPABLE = ['department', 'category', 'processType', 'industry', 'businessUnit', 'geography', 'customerType', 'compliance'];
                const prop = searchProperty.replace(/^draft\./, '');
                if (!DICT_CAPABLE.includes(prop)) { setValueSuggestions([]); return; }
                const query = gql`query FormsListDictionary($k:String!){ listDictionary(kind:$k) }`;
                const res = await apolloClient.query<Record<string, any>>({ query, variables: { k: kind } });
                const list = (res?.data?.listDictionary as string[]) || [];
                setValueSuggestions(Array.isArray(list) ? list.slice(0, 200) : []);
            } catch { setValueSuggestions([]); }
        };
        run();
    }, [apolloClient, searchProperty, selectedKinds, initialType]);

    // Actor suggestions from ActorDescriptor drafts
    useEffect(() => {
        const run = async () => {
            try {
                const LIST_DRAFTS = gql`
                  query ListActorDescriptors($tenant: String!) {
                    listDrafts(input: { tenant: $tenant, kind: "ActorDescriptor" }) {
                      key
                      owner
                      updatedBy
                    }
                  }
                `;
                const tenantId = (() => { try { return tenant || (globalThis as any).__TENANT__ || sessionStorage.getItem('tenant') || 'test'; } catch { return tenant || 'test'; } })();
                const res = await apolloClient.query<Record<string, any>>({ query: LIST_DRAFTS, variables: { tenant: tenantId } });
                const list = (res?.data?.listDrafts as Array<any>) || [];
                const names = Array.from(new Set(list.map((r) => String(r.owner || r.updatedBy || r.key)).filter(Boolean))).slice(0, 300);
                setActorSuggestions(names);
            } catch {
                setActorSuggestions([]);
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <WorkflowComponentsSearch
            title={title}
            allKinds={allKinds}
            selectedKinds={selectedKinds}
            onKindsChange={setSelectedKinds}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            propertyList={propertyList}
            selectedProperty={searchProperty}
            onPropertyChange={setSearchProperty}
            fullText={fullText}
            onFullTextChange={setFullText}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            columnsByCategory={columnsByCategory as any}
            onToggleColumn={onToggleColumn}
            valueSuggestions={valueSuggestions}
            actorSuggestions={actorSuggestions}
            onApply={(cfg) => {
                setSelectedKinds(cfg.kinds);
                setViewMode(cfg.viewMode);
                setDateRange(cfg.dateRange);
                setLive(!!cfg.live);
                onApply(cfg);
            }}
            onClear={() => {
                setViewMode('standard');
                setFullText('');
                setSearchProperty('name');
                setDateRange([null, null]);
                setLive(false);
                onClear();
            }}
            collapsible
        />
    );
};

export default WorkflowComponentsController;


